import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery,
    Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { AiFillBulb } from "react-icons/ai";

// ─── Styled Components (matching established C++ series pattern) ─
const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

// ─── Reusable UI helpers ───────────────────────────────────────
const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && (
                <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    </Box>
);

const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)",
        border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`,
        borderRadius: "12px",
        p: "16px 18px",
        mb: 2,
    }}>
        {title && (
            <Typography sx={{
                fontWeight: 700, color, fontSize: "0.82rem",
                mb: 1, textTransform: "uppercase", letterSpacing: "0.06em",
            }}>
                {title}
            </Typography>
        )}
        {children}
    </Box>
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs
        value={value} onChange={onChange}
        variant="scrollable" scrollButtons="auto"
        sx={{
            mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
            "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
            "& .Mui-selected": { color: "goldenrod !important" },
            "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
        }}
    >
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

const PillChip = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700,
        fontSize: "0.72rem", height: 22,
        border: `1px solid ${color}44`,
        "& .MuiChip-label": { px: 1 },
    }} />
);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 1: Classes
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const classTabs = [
    {
        label: "Basic Class",
        code: `// ── Defining a class ─────────────────────────────────────────
#include <iostream>
#include <string>

class BankAccount {
// ── Access specifiers ─────────────────────────────────────────
private:                          // only accessible inside this class
    std::string owner;
    double balance;
    int    accountNumber;

public:                           // accessible from anywhere
    // ── Constructor — initialises the object ──────────────────
    BankAccount(std::string name, double initialBalance, int accNum)
        : owner(name),            // member initialiser list (preferred)
          balance(initialBalance),
          accountNumber(accNum)
    {
        std::cout << "Account created for " << owner << "\\n";
    }

    // ── Member functions (methods) ────────────────────────────
    void deposit(double amount) {
        if (amount > 0) balance += amount;
    }

    bool withdraw(double amount) {
        if (amount > balance) return false;
        balance -= amount;
        return true;
    }

    // ── Getter (accessor) — read private data safely ──────────
    double getBalance() const { return balance; }
    std::string getOwner() const { return owner; }

    void printStatement() const {
        std::cout << "Account: " << accountNumber
                  << "  Owner: " << owner
                  << "  Balance: $" << balance << "\\n";
    }
};`,
    },
    {
        label: "Constructor Types",
        code: `#include <string>

class Rectangle {
    double width, height;
public:
    // ── Default constructor (no args) ─────────────────────────
    Rectangle() : width(0), height(0) {}

    // ── Parameterised constructor ─────────────────────────────
    Rectangle(double w, double h) : width(w), height(h) {}

    // ── Copy constructor — makes a copy of another object ─────
    Rectangle(const Rectangle& other)
        : width(other.width), height(other.height) {
        std::cout << "Copy constructor called\\n";
    }

    // ── Move constructor (C++11) — steals resources from rvalue ─
    Rectangle(Rectangle&& other) noexcept
        : width(other.width), height(other.height) {
        other.width = other.height = 0;  // leave source in valid state
        std::cout << "Move constructor called\\n";
    }

    // ── Destructor — called when object goes out of scope ─────
    ~Rectangle() {
        std::cout << "Rectangle destroyed\\n";
    }

    double area()      const { return width * height; }
    double perimeter() const { return 2 * (width + height); }
};

int main() {
    Rectangle r1;                    // default constructor
    Rectangle r2(5.0, 3.0);          // parameterised
    Rectangle r3 = r2;               // copy constructor
    Rectangle r4 = std::move(r2);    // move constructor
}`,
    },
    {
        label: "Member Functions",
        code: `#include <iostream>
#include <string>
#include <cmath>

class Circle {
    double radius;
public:
    Circle(double r) : radius(r) {}

    // ── const member function — does NOT modify the object ─────
    double area()        const { return 3.14159 * radius * radius; }
    double circumference() const { return 2 * 3.14159 * radius; }

    // ── Non-const — modifies the object ───────────────────────
    void scale(double factor) { radius *= factor; }

    // ── static member function — belongs to class, not instance ─
    static Circle unitCircle() { return Circle(1.0); }  // factory method

    // ── Inline definition vs out-of-class definition ──────────
    double getRadius() const;  // declared here, defined below
};

// ── Out-of-class definition uses ClassName:: ─────────────────
double Circle::getRadius() const {
    return radius;
}

// ── friend function — non-member but accesses private ─────────
class Point {
    int x, y;
    friend double distance(const Point& a, const Point& b); // declare friend
public:
    Point(int x, int y) : x(x), y(y) {}
};

double distance(const Point& a, const Point& b) {
    return std::sqrt((a.x-b.x)*(a.x-b.x) + (a.y-b.y)*(a.y-b.y)); // accesses private x,y
}`,
    },
    {
        label: "Operator Overloading",
        code: `#include <iostream>

class Vector2D {
public:
    double x, y;
    Vector2D(double x=0, double y=0) : x(x), y(y) {}

    // ── Overload + operator ───────────────────────────────────
    Vector2D operator+(const Vector2D& other) const {
        return {x + other.x, y + other.y};
    }

    // ── Overload - operator ───────────────────────────────────
    Vector2D operator-(const Vector2D& other) const {
        return {x - other.x, y - other.y};
    }

    // ── Overload * for scalar multiplication ──────────────────
    Vector2D operator*(double scalar) const {
        return {x * scalar, y * scalar};
    }

    // ── Overload == for equality ──────────────────────────────
    bool operator==(const Vector2D& other) const {
        return x == other.x && y == other.y;
    }

    // ── Overload << for printing (non-member friend) ──────────
    friend std::ostream& operator<<(std::ostream& os, const Vector2D& v) {
        return os << "(" << v.x << ", " << v.y << ")";
    }
};

int main() {
    Vector2D a(1, 2), b(3, 4);
    Vector2D c = a + b;          // calls operator+
    Vector2D d = c * 2.0;        // calls operator*
    std::cout << a << " + " << b << " = " << c;  // uses operator<<
}`,
    },
];

const classMembers = [
    { member: "Data members",       access: "private / protected", desc: "Variables that store object state",              example: "double balance;" },
    { member: "Member functions",   access: "public / private",    desc: "Functions operating on the object",             example: "void deposit(double);" },
    { member: "Constructor",        access: "public",              desc: "Special function called on creation",           example: "BankAccount(string, double);" },
    { member: "Destructor",         access: "public",              desc: "Called when object is destroyed — cleanup",     example: "~BankAccount();" },
    { member: "static member",      access: "any",                 desc: "Shared across ALL instances of the class",      example: "static int count;" },
    { member: "const member fn",    access: "public",              desc: "Cannot modify any data members — read-only",    example: "double getBalance() const;" },
    { member: "friend function",    access: "non-member",          desc: "External function granted private access",      example: "friend ostream& operator<<(...);" },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 2: Objects
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const objectTabs = [
    {
        label: "Creating Objects",
        code: `#include <iostream>
#include <memory>   // for smart pointers

class Dog {
    std::string name;
    int         age;
public:
    Dog(std::string n, int a) : name(n), age(a) {}
    void bark() const { std::cout << name << " says: Woof!\\n"; }
    std::string getName() const { return name; }
};

int main() {
    // ── Stack allocation (automatic storage) ─────────────────
    Dog d1("Rex", 3);           // created on stack — destroyed automatically
    d1.bark();                  // dot operator to call method
    d1.getName();

    // ── Heap allocation with raw pointer ─────────────────────
    Dog* d2 = new Dog("Buddy", 5);   // created on heap — YOU manage lifetime
    d2->bark();                       // arrow operator for pointer
    delete d2;                        // MUST delete to avoid memory leak!
    d2 = nullptr;

    // ── Heap allocation with smart pointer (PREFERRED C++11) ──
    auto d3 = std::make_unique<Dog>("Max", 2);   // unique ownership
    d3->bark();                                   // auto-deleted when d3 goes out of scope

    auto d4 = std::make_shared<Dog>("Luna", 4);  // shared ownership
    auto d5 = d4;                                // both own Luna
    d4->bark();
    // Luna deleted only when BOTH d4 and d5 are gone
}`,
    },
    {
        label: "Object Arrays & Vectors",
        code: `#include <vector>
#include <iostream>

class Student {
    std::string name;
    double      gpa;
public:
    Student(std::string n, double g) : name(n), gpa(g) {}
    void print() const {
        std::cout << name << " GPA: " << gpa << "\\n";
    }
    double getGpa() const { return gpa; }
    std::string getName() const { return name; }
};

int main() {
    // ── Array of objects ──────────────────────────────────────
    Student arr[3] = {           // all 3 must be constructable
        {"Alice", 3.9},
        {"Bob",   3.5},
        {"Carol", 3.7}
    };
    arr[0].print();

    // ── Vector of objects (preferred — dynamic size) ──────────
    std::vector<Student> students;
    students.emplace_back("Dave",  3.8);   // constructs in-place (no copy)
    students.emplace_back("Eve",   3.6);
    students.push_back({"Frank", 3.4});    // push_back also works

    for (const auto& s : students)
        s.print();

    // ── Sort vector of objects ─────────────────────────────────
    std::sort(students.begin(), students.end(),
        [](const Student& a, const Student& b){
            return a.getGpa() > b.getGpa();  // descending GPA
        });
}`,
    },
    {
        label: "this Pointer",
        code: `#include <iostream>
#include <string>

class Builder {
    std::string name;
    int         floors;
    std::string material;

public:
    Builder() : floors(1), material("concrete") {}

    // ── this pointer — points to the calling object ───────────
    // Used when: param shadows member, or for method chaining

    Builder& setName(std::string name) {
        this->name = name;    // this->name = member, name = param
        return *this;         // return reference to self for chaining
    }

    Builder& setFloors(int floors) {
        this->floors = floors;
        return *this;
    }

    Builder& setMaterial(std::string mat) {
        material = mat;       // no ambiguity — mat vs material
        return *this;
    }

    void build() {
        std::cout << "Building: " << floors << "-floor "
                  << material << " structure called " << name << "\\n";
    }
};

int main() {
    // ── Method chaining enabled by returning *this ─────────────
    Builder()
        .setName("TowerX")
        .setFloors(40)
        .setMaterial("steel")
        .build();
    // Output: Building: 40-floor steel structure called TowerX
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 3: Inheritance
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const inheritanceTabs = [
    {
        label: "Single Inheritance",
        code: `#include <iostream>
#include <string>

// ── BASE CLASS (parent) ───────────────────────────────────────
class Animal {
protected:                         // accessible in derived classes!
    std::string name;
    int         age;

public:
    Animal(std::string n, int a) : name(n), age(a) {}

    void breathe() const {
        std::cout << name << " is breathing\\n";
    }

    virtual void speak() const {   // virtual = can be overridden
        std::cout << name << " makes a sound\\n";
    }

    virtual ~Animal() {}           // ALWAYS make destructor virtual in base!
};

// ── DERIVED CLASS (child) inherits from Animal ───────────────
class Dog : public Animal {        // public inheritance — most common
    std::string breed;

public:
    Dog(std::string n, int a, std::string b)
        : Animal(n, a),            // call base constructor FIRST
          breed(b) {}

    void speak() const override {  // override base class virtual
        std::cout << name << " says: Woof!\\n";  // name from protected base
    }

    void fetch() const {           // new method unique to Dog
        std::cout << name << " fetches the ball!\\n";
    }
};

int main() {
    Animal a("Generic", 5);
    Dog    d("Rex", 3, "Labrador");

    a.speak();           // "Generic makes a sound"
    d.speak();           // "Rex says: Woof!"  ← overridden!
    d.breathe();         // ✅ inherited from Animal
    d.fetch();           // ✅ Dog-specific
}`,
    },
    {
        label: "Multi-level & Multiple",
        code: `// ── MULTI-LEVEL inheritance ─────────────────────────────────
class LivingThing {
public:
    void grow()  { std::cout << "Growing\\n"; }
    void die()   { std::cout << "Life ended\\n"; }
    virtual ~LivingThing() {}
};

class Animal : public LivingThing {
public:
    void move()  { std::cout << "Moving\\n"; }
    void eat()   { std::cout << "Eating\\n"; }
};

class Dog : public Animal {       // Dog → Animal → LivingThing (3 levels)
public:
    void bark()  { std::cout << "Barking!\\n"; }
};

// Dog inherits: bark, move, eat, grow, die (from all levels!)

// ── MULTIPLE inheritance ──────────────────────────────────────
class Flyable {
public:
    virtual void fly() { std::cout << "Flying!\\n"; }
    virtual ~Flyable() {}
};

class Swimmable {
public:
    virtual void swim() { std::cout << "Swimming!\\n"; }
    virtual ~Swimmable() {}
};

// Duck inherits from BOTH Flyable AND Swimmable
class Duck : public Animal, public Flyable, public Swimmable {
public:
    void fly()  override { std::cout << "Duck flies low\\n"; }
    void swim() override { std::cout << "Duck paddles\\n"; }
    void quack()         { std::cout << "Quack!\\n"; }
};

Duck d;
d.fly();   // Duck flies low
d.swim();  // Duck paddles
d.eat();   // inherited from Animal`,
    },
    {
        label: "Virtual & Polymorphism",
        code: `#include <iostream>
#include <vector>
#include <memory>

class Shape {
public:
    // ── Pure virtual = MUST override in derived class ─────────
    virtual double area()        const = 0;   // = 0 means abstract
    virtual double perimeter()   const = 0;
    virtual std::string name()   const = 0;

    // ── Non-pure virtual — has default implementation ─────────
    virtual void describe() const {
        std::cout << name() << "  area=" << area()
                  << "  perimeter=" << perimeter() << "\\n";
    }

    virtual ~Shape() {}
};
// Shape is ABSTRACT — cannot instantiate Shape directly

class Circle : public Shape {
    double r;
public:
    Circle(double r) : r(r) {}
    double area()      const override { return 3.14159 * r * r; }
    double perimeter() const override { return 2 * 3.14159 * r; }
    std::string name() const override { return "Circle"; }
};

class Rectangle : public Shape {
    double w, h;
public:
    Rectangle(double w, double h) : w(w), h(h) {}
    double area()      const override { return w * h; }
    double perimeter() const override { return 2*(w+h); }
    std::string name() const override { return "Rectangle"; }
};

int main() {
    // ── Polymorphism — base pointer, derived behaviour ─────────
    std::vector<std::unique_ptr<Shape>> shapes;
    shapes.push_back(std::make_unique<Circle>(5));
    shapes.push_back(std::make_unique<Rectangle>(4, 6));
    shapes.push_back(std::make_unique<Circle>(3));

    for (const auto& s : shapes)
        s->describe();         // calls the RIGHT version at runtime!
    // Circle   area=78.54  perimeter=31.42
    // Rectangle area=24    perimeter=20
    // Circle   area=28.27  perimeter=18.85
}`,
    },
    {
        label: "Access Specifiers",
        code: `// ── How access specifiers affect inheritance ─────────────────
//
//  Base member   │ public inherit │ protected inherit │ private inherit
//  ──────────────┼────────────────┼───────────────────┼────────────────
//  public        │ public         │ protected         │ private
//  protected     │ protected      │ protected         │ private
//  private       │ NOT inherited  │ NOT inherited     │ NOT inherited
//
//  ▶ public    — "IS-A" relationship (most common: Dog IS-A Animal)
//  ▶ protected — rarely used (implementation inheritance)
//  ▶ private   — "IMPLEMENTED-IN-TERMS-OF" relationship

class Base {
public:    int pub   = 1;   // ← accessible everywhere
protected: int prot  = 2;   // ← accessible in Base + derived classes
private:   int priv  = 3;   // ← only accessible inside Base
};

class PublicChild : public Base {
    void test() {
        pub  = 10;   // ✅ still public
        prot = 20;   // ✅ still protected (accessible here)
        // priv = 30; ❌ private — NOT accessible
    }
};

class PrivateChild : private Base {
    void test() {
        pub  = 10;   // ✅ becomes private in PrivateChild
        prot = 20;   // ✅ becomes private in PrivateChild
    }
};

// Outside the classes:
PublicChild pc;
pc.pub;       // ✅ public inheritance preserved public
// pc.prot;   // ❌ protected — not accessible outside
PrivateChild prv;
// prv.pub;   // ❌ private inheritance made pub private!`,
    },
];

const inheritanceComparison = [
    { type: "Single",      syntax: "class Dog : public Animal",                  desc: "One parent class",                  use: "Most common — clear hierarchy" },
    { type: "Multi-level", syntax: "Dog→Animal→LivingThing",                    desc: "Chain of parent classes",            use: "Deep hierarchies (use with care)" },
    { type: "Multiple",    syntax: "class Duck : public Flyable, public Swim",   desc: "Two or more parent classes",         use: "Mixin-style composition of behaviours" },
    { type: "Virtual",     syntax: "class B : virtual public A",                 desc: "Solves diamond problem",             use: "Multiple inheritance with shared base" },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 4: Encapsulation
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const encapTabs = [
    {
        label: "Getters & Setters",
        code: `#include <iostream>
#include <string>
#include <stdexcept>

class Employee {
private:                               // DATA is hidden ─ encapsulated
    std::string name;
    double      salary;
    int         age;
    std::string department;

public:
    Employee(std::string n, double s, int a, std::string dept)
        : name(n), age(a), department(dept) {
        setSalary(s);                  // validate via setter even in constructor!
    }

    // ── Getters — safe READ access ────────────────────────────
    std::string getName()       const { return name; }
    double      getSalary()     const { return salary; }
    int         getAge()        const { return age; }
    std::string getDepartment() const { return department; }

    // ── Setters — validated WRITE access ─────────────────────
    void setName(const std::string& n) {
        if (n.empty()) throw std::invalid_argument("Name cannot be empty");
        name = n;
    }

    void setSalary(double s) {
        if (s < 0)      throw std::invalid_argument("Salary cannot be negative");
        if (s > 1000000) throw std::invalid_argument("Salary exceeds maximum");
        salary = s;
    }

    void setAge(int a) {
        if (a < 18 || a > 65)
            throw std::out_of_range("Age must be between 18 and 65");
        age = a;
    }

    void promote(double raise, std::string newDept) {
        setSalary(salary + raise);     // business logic lives here
        department = newDept;
        std::cout << name << " promoted to " << newDept << "\\n";
    }
};

int main() {
    Employee emp("Alice", 75000, 30, "Engineering");
    std::cout << emp.getName() << ": $" << emp.getSalary() << "\\n";

    emp.setSalary(80000);              // validated!
    // emp.setSalary(-500);            // ← throws invalid_argument

    emp.promote(10000, "Senior Engineering");
}`,
    },
    {
        label: "Data Hiding",
        code: `#include <iostream>
#include <vector>
#include <string>

// ── WHY data hiding matters — comparison ─────────────────────

// ❌ BAD: No encapsulation — data is exposed
struct BankBad {
    std::string owner;
    double balance = 0;     // anyone can set balance = -99999!
    int    pin = 1234;      // PIN is public!  Security disaster
};

// ✅ GOOD: Fully encapsulated
class BankGood {
private:
    std::string owner;
    double      balance;
    int         pin;
    std::vector<std::string> transactionLog;

    // private helper — implementation detail, hidden from user
    void log(const std::string& msg) {
        transactionLog.push_back(msg);
    }

public:
    BankGood(std::string owner, int pin)
        : owner(owner), balance(0.0), pin(pin) {}

    bool deposit(double amt) {
        if (amt <= 0) return false;
        balance += amt;
        log("Deposit: $" + std::to_string(amt));
        return true;
    }

    bool withdraw(double amt, int enteredPin) {
        if (enteredPin != pin)  { log("FAILED: wrong PIN"); return false; }
        if (amt > balance)      { log("FAILED: insufficient funds"); return false; }
        balance -= amt;
        log("Withdraw: $" + std::to_string(amt));
        return true;
    }

    double getBalance(int enteredPin) const {
        return (enteredPin == pin) ? balance : -1;
    }

    void printLog() const {
        for (const auto& entry : transactionLog)
            std::cout << "  " << entry << "\\n";
    }
};`,
    },
    {
        label: "Class vs Struct",
        code: `// ── class vs struct in C++ ───────────────────────────────────
//
//  The ONLY technical difference:
//    struct  — members are PUBLIC by default
//    class   — members are PRIVATE by default
//
//  Convention:
//    Use struct for plain data holders (no invariants, minimal logic)
//    Use class  for entities with business logic and enforced invariants

// ── struct: pure data aggregate ──────────────────────────────
struct Point {
    double x, y;           // public by default
    double distanceTo(const Point& other) const {
        return std::sqrt((x-other.x)*(x-other.x) + (y-other.y)*(y-other.y));
    }
};
Point p = {3.0, 4.0};     // aggregate initialisation
std::cout << p.x;         // direct access — fine for simple data

// ── class: encapsulated entity ───────────────────────────────
class Temperature {
    double celsius;        // private by default
public:
    explicit Temperature(double c) : celsius(c) {}

    double toCelsius()    const { return celsius; }
    double toFahrenheit() const { return celsius * 9.0/5.0 + 32; }
    double toKelvin()     const { return celsius + 273.15; }

    bool isAboveBoiling() const { return celsius >= 100.0; }

    // Enforces invariant: temperature cannot be below absolute zero
    void setCelsius(double c) {
        if (c < -273.15) throw std::invalid_argument("Below absolute zero");
        celsius = c;
    }
};
Temperature t(100.0);
std::cout << t.toFahrenheit();   // 212`,
    },
    {
        label: "RAII Pattern",
        code: `#include <fstream>
#include <stdexcept>
#include <memory>

// ── RAII: Resource Acquisition Is Initialisation ─────────────
// Tie resource lifetime to object lifetime.
// Constructor acquires. Destructor releases. No leaks possible.

class FileHandler {
    std::ofstream file;
    std::string   path;
public:
    FileHandler(const std::string& filename) : path(filename) {
        file.open(filename);
        if (!file.is_open())
            throw std::runtime_error("Cannot open: " + filename);
        std::cout << "File opened: " << filename << "\\n";
    }

    ~FileHandler() {
        if (file.is_open()) {
            file.close();
            std::cout << "File closed: " << path << "\\n";
        }
    }

    void write(const std::string& data) { file << data << "\\n"; }

    // Prevent copying (resource can't be duplicated)
    FileHandler(const FileHandler&) = delete;
    FileHandler& operator=(const FileHandler&) = delete;
};

void processData() {
    FileHandler fh("output.txt");  // file opened here
    fh.write("Line 1");
    fh.write("Line 2");
    // fh.write(...) might throw — no problem!
}   // ← fh destructor runs HERE automatically — file always closed!

// ── Smart pointers are the canonical RAII tools ───────────────
void smartPointerRAII() {
    auto ptr = std::make_unique<int[]>(1000);  // 1000 ints allocated
    ptr[0] = 42;
    // ... use ptr ...
}   // ← memory freed automatically — no delete needed!`,
    },
];

// Four Pillars comparison data
const fourPillars = [
    {
        pillar: "Encapsulation",
        icon: "🔒",
        color: "#f0883e",
        tagline: "Bundle data + methods; hide internals",
        benefit: "Prevents invalid state; easy to change internals",
        example: "private data + public getters/setters",
    },
    {
        pillar: "Inheritance",
        icon: "🧬",
        color: "#58a6ff",
        tagline: "Derive new class from existing class",
        benefit: "Code reuse; models IS-A relationships",
        example: "class Dog : public Animal",
    },
    {
        pillar: "Polymorphism",
        icon: "🎭",
        color: "#7ee787",
        tagline: "Same interface, different implementations",
        benefit: "Extensible design; Open/Closed Principle",
        example: "virtual void speak() — Dog, Cat, Bird",
    },
    {
        pillar: "Abstraction",
        icon: "🎯",
        color: "#a855f7",
        tagline: "Show essential features, hide complexity",
        benefit: "Simplifies usage; reduces coupling",
        example: "Pure virtual (=0) abstract base classes",
    },
];

// Access specifiers summary
const accessRows = [
    { spec: "public",    inside: "✅", derived: "✅", outside: "✅", use: "Interface — what users can call" },
    { spec: "protected", inside: "✅", derived: "✅", outside: "❌", use: "For derived classes to use/extend" },
    { spec: "private",   inside: "✅", derived: "❌", outside: "❌", use: "Implementation details — fully hidden" },
];

// OOP checklist
const oopChecklist = [
    { category: "Classes",       color: "#f0883e", items: ["✅ Data members are private", "✅ Constructor initialises all members", "✅ Destructor is virtual in base class", "✅ Use member initialiser list over assignment", "✅ Mark read-only methods const", "❌ Don't put class body in .h without inline guard"] },
    { category: "Objects",       color: "#58a6ff", items: ["✅ Prefer stack allocation over new/delete", "✅ Use smart pointers for heap objects", "✅ Apply RAII for resources (files, locks, memory)", "✅ Rule of 5: destructor, copy+move ctor, copy+move assign", "✅ = delete unwanted copy/move if resource is unique", "❌ Don't use raw new — use make_unique / make_shared"] },
    { category: "Inheritance",   color: "#7ee787", items: ["✅ Base class destructor MUST be virtual", "✅ Use override keyword on every override", "✅ Use public inheritance for IS-A relationships", "✅ Prefer composition over deep inheritance trees", "✅ Abstract base classes use pure virtual (= 0)", "❌ Avoid multiple inheritance unless it's mixins"] },
    { category: "Encapsulation", color: "#a855f7", items: ["✅ All data members private by default", "✅ Validate input in setters", "✅ Expose minimal public interface", "✅ Use struct only for simple data aggregates", "✅ Private helpers hide implementation details", "❌ Don't return non-const references to private data"] },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CppOOP() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [classTab,  setClassTab]  = useState(0);
    const [objTab,    setObjTab]    = useState(0);
    const [inhTab,    setInhTab]    = useState(0);
    const [encapTab,  setEncapTab]  = useState(0);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800, color: "goldenrod", display: "inline-block",
                        px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                        border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                    }}
                >
                    C++ OOP
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    Object-Oriented Programming in C++
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["Classes", "Objects", "Inheritance", "Encapsulation"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{
                            bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)",
                            border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem",
                        }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 3 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.85, color: "rgba(255,255,255,0.8)" }}>
                        <strong style={{ color: "goldenrod" }}>Object-Oriented Programming (OOP)</strong> is a programming paradigm based on
                        objects and classes. It enhances{" "}
                        <strong style={{ color: "goldenrod" }}>code organisation, reusability</strong>, and{" "}
                        <strong style={{ color: "goldenrod" }}>modularity</strong> by modelling real-world entities
                        as objects that bundle data and behaviour together.
                    </Typography>
                </Box>
            </Box>

            {/* ── Four Pillars overview ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 2 }}>
                    {fourPillars.map((p, i) => (
                        <Box key={i} sx={{
                            bgcolor: "rgba(26,14,52,0.9)", border: `1px solid ${p.color}44`,
                            borderTop: `3px solid ${p.color}`, borderRadius: "14px",
                            p: "16px 14px", textAlign: "center",
                            transition: "transform 0.2s, box-shadow 0.2s",
                            "&:hover": { transform: "translateY(-3px)", boxShadow: `0 8px 24px ${p.color}22` },
                        }}>
                            <Typography sx={{ fontSize: "1.8rem", mb: 0.5 }}>{p.icon}</Typography>
                            <Typography sx={{ fontWeight: 800, color: p.color, fontSize: "0.85rem", mb: 0.5 }}>{p.pillar}</Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>{p.tagline}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — CLASSES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. Classes
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            A <strong>class</strong> is a blueprint that defines the <em>data</em> (attributes)
                            and <em>behaviour</em> (methods) that objects of that type will have.
                            Think of a class as the architectural drawing; an object is the actual building.
                        </Typography>

                        {/* Class vs Object visual */}
                        <ConceptCard title="Class = Blueprint · Object = Instance" color="#f0883e">
                            <CodeBlock>{`//  Class  →  like a cookie cutter (the template)
//  Object →  like a cookie (a specific instance)
//
//  class Car {              ← CLASS: defines the template
//      string brand;        ← attribute (every Car has a brand)
//      int    speed;        ← attribute
//      void   accelerate(); ← method (behaviour)
//  };
//
//  Car toyota("Corolla", 0);  ← OBJECT 1: specific Toyota
//  Car honda ("Civic",   0);  ← OBJECT 2: specific Honda
//  Car bmw   ("M3",      0);  ← OBJECT 3: specific BMW
//
//  Same class, three different objects — each with their own data`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={classTab} onChange={(_, v) => setClassTab(v)} tabs={classTabs.map(t => t.label)} />
                        <CodeBlock>{classTabs[classTab].code}</CodeBlock>

                        {/* Class members table */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="Class Members Reference" subtitle="Everything a class can contain" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Member", "Typical Access", "Description", "Example"]} cols="140px 120px 1fr 1fr" />}
                            {classMembers.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Box sx={{ display: "flex", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                                            <Typography sx={{ fontWeight: 700, color: "goldenrod", fontSize: "0.88rem" }}>{row.member}</Typography>
                                            <PillChip label={row.access} color="#58a6ff" />
                                        </Box>
                                        <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", mb: 0.5 }}>{row.desc}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.75rem" }}>{row.example}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "140px 120px 1fr 1fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: "0.83rem", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontWeight: 700, color: "goldenrod", fontFamily: "monospace", fontSize: "0.8rem" }}>{row.member}</Box>
                                        <PillChip label={row.access} color="#58a6ff" />
                                        <Box sx={{ opacity: 0.75, fontSize: "0.81rem" }}>{row.desc}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.example}</Box>
                                    </Box>
                                )
                            )}
                        </Box>

                        {/* Access specifiers */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🔑</span>} title="Access Specifiers" subtitle="Control who can see what inside a class" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 2 }}>
                            {accessRows.map((row, i) => {
                                const colors = ["#56d364", "#fbbf24", "#f85149"];
                                return (
                                    <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${colors[i]}33`, borderTop: `3px solid ${colors[i]}`, borderRadius: "12px", p: "16px" }}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 800, color: colors[i], fontSize: "1rem", mb: 1 }}>{row.spec}</Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
                                            {[["Inside class", row.inside], ["Derived class", row.derived], ["Outside class", row.outside]].map(([label, val]) => (
                                                <Box key={label} sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem" }}>
                                                    <Typography sx={{ opacity: 0.6, fontSize: "0.8rem" }}>{label}</Typography>
                                                    <Typography sx={{ fontSize: "0.85rem" }}>{val}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        <Typography sx={{ fontSize: "0.78rem", color: colors[i], opacity: 0.85, fontStyle: "italic" }}>{row.use}</Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — OBJECTS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. Objects
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            An <strong>object</strong> is a specific instance of a class — a concrete entity
                            that occupies memory and has its own copy of the class's data members.
                            Creating an object is called <em>instantiation</em>.
                        </Typography>

                        <ConceptCard title="Stack vs Heap Allocation" color="#e879f9">
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                                {[
                                    { title: "Stack (automatic)", color: "#56d364", points: ["Declared as: Dog d(\"Rex\", 3);", "Destroyed automatically when scope ends", "Fast — no allocator overhead", "Use for most local objects"] },
                                    { title: "Heap (dynamic)", color: "#f0883e", points: ["Declared with new / make_unique", "YOU control lifetime (or smart ptr does)", "Needed for: unknown size, long lifetime", "Always pair new with delete (or use RAII)"] },
                                ].map((side, j) => (
                                    <Box key={j} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${side.color}33`, borderRadius: "10px", p: "12px 14px" }}>
                                        <Typography sx={{ fontWeight: 700, color: side.color, fontSize: "0.82rem", mb: 1 }}>{side.title}</Typography>
                                        {side.points.map((pt, k) => (
                                            <Typography key={k} sx={{ fontSize: "0.8rem", opacity: 0.75, lineHeight: 1.7 }}>• {pt}</Typography>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </ConceptCard>

                        <StyledTabs value={objTab} onChange={(_, v) => setObjTab(v)} tabs={objectTabs.map(t => t.label)} />
                        <CodeBlock>{objectTabs[objTab].code}</CodeBlock>

                        {/* Smart pointer quick guide */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🧠</span>} title="Smart Pointer Quick Guide" subtitle="Prefer these over raw new/delete in modern C++" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 2 }}>
                            {[
                                { name: "unique_ptr<T>", color: "#58a6ff", owns: "One owner only", code: "auto p = make_unique<Dog>(\"Rex\");", when: "Default choice — sole ownership" },
                                { name: "shared_ptr<T>", color: "#7ee787", owns: "Shared ownership", code: "auto p = make_shared<Dog>(\"Max\");", when: "Multiple owners need same object" },
                                { name: "weak_ptr<T>",   color: "#fbbf24", owns: "Non-owning observer", code: "weak_ptr<Dog> w = sharedPtr;", when: "Break circular shared_ptr references" },
                            ].map((sp, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${sp.color}33`, borderRadius: "12px", p: "14px 16px" }}>
                                    <Typography sx={{ fontFamily: "monospace", fontWeight: 800, color: sp.color, fontSize: "0.85rem", mb: 0.5 }}>{sp.name}</Typography>
                                    <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", mb: 1 }}>Ownership: {sp.owns}</Typography>
                                    <CodeBlock sx={{ fontSize: "0.75rem", p: "8px 12px", mb: 1, "&::before": { content: '""' } }}>{sp.code}</CodeBlock>
                                    <Typography sx={{ fontSize: "0.78rem", color: sp.color, opacity: 0.85 }}>✅ {sp.when}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — INHERITANCE
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. Inheritance
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <strong>Inheritance</strong> lets a new class (derived/child) acquire the properties
                            and methods of an existing class (base/parent). It models <em>IS-A</em> relationships
                            and enables code reuse, extension, and polymorphic behaviour.
                        </Typography>

                        {/* Hierarchy diagram */}
                        <ConceptCard title="Inheritance Hierarchy Visualised" color="#58a6ff">
                            <CodeBlock>{`//   Animal (Base)          ← breathe(), eat(), sleep(), virtual speak()
//   ├── Dog : Animal       ← + bark(), fetch()   │ overrides speak()
//   │   └── GuideDog : Dog ← + guide()           │ further specialised
//   ├── Cat : Animal       ← + purr(), scratch()  │ overrides speak()
//   └── Bird : Animal      ← + fly(), sing()      │ overrides speak()
//
//   Animal* ptr = new Dog("Rex", 3);
//   ptr->speak();   ←  calls DOG's speak() at runtime  (polymorphism!)
//   ptr->eat();     ←  calls Animal's eat() (not overridden)
//
//   delete ptr;     ←  calls ~Dog() then ~Animal()  (virtual destructor!)`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={inhTab} onChange={(_, v) => setInhTab(v)} tabs={inheritanceTabs.map(t => t.label)} />
                        <CodeBlock>{inheritanceTabs[inhTab].code}</CodeBlock>

                        {/* Inheritance types table */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>📊</span>} title="Inheritance Types" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Type", "Syntax", "Description", "When to Use"]} />}
                            {inheritanceComparison.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Typography sx={{ fontWeight: 700, color: "#58a6ff", fontSize: "0.88rem", mb: 0.5 }}>{row.type}</Typography>
                                        <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.75rem", mb: 0.5 }}>{row.syntax}</Typography>
                                        <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)" }}>{row.desc} — {row.use}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: "0.83rem", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontWeight: 700, color: "#58a6ff" }}>{row.type}</Box>
                                        <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.syntax}</Box>
                                        <Box sx={{ opacity: 0.7, fontSize: "0.81rem" }}>{row.desc}</Box>
                                        <Box sx={{ opacity: 0.75, fontSize: "0.81rem" }}>{row.use}</Box>
                                    </Box>
                                )
                            )}
                        </Box>

                        {/* virtual vs override vs final */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🎭</span>} title="virtual / override / final Keywords" />
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {[
                                { kw: "virtual",   color: "#7ee787", desc: "Base class: marks function as overridable. Without virtual, derived class hides rather than overrides!",                   code: "virtual void speak() const;" },
                                { kw: "override",  color: "#fbbf24", desc: "Derived class: confirms you're overriding a virtual. Compiler error if base has no matching virtual — prevents typos.", code: "void speak() const override;" },
                                { kw: "final",     color: "#f85149", desc: "Prevents further overriding or inheritance. Use to lock down a class or method.",                                       code: "void speak() const final;\nclass Sealed final : public Base {};" },
                                { kw: "= 0",       color: "#e879f9", desc: "Pure virtual — makes the class abstract. Derived classes MUST implement this function to be instantiable.",           code: "virtual double area() const = 0;" },
                            ].map((row, i) => (
                                <Box key={i} sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "90px 1fr 1fr", gap: "12px", bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${row.color}22`, borderLeft: `3px solid ${row.color}`, borderRadius: "12px", px: 2, py: "12px", alignItems: "center" }}>
                                    <Typography sx={{ fontFamily: "monospace", fontWeight: 800, color: row.color, fontSize: "0.9rem" }}>{row.kw}</Typography>
                                    <Typography sx={{ fontSize: "0.82rem", opacity: 0.8, lineHeight: 1.6 }}>{row.desc}</Typography>
                                    <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{row.code}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — ENCAPSULATION
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. Encapsulation
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <strong>Encapsulation</strong> bundles data and the methods that operate on that
                            data into a single unit (the class), and <em>restricts direct access</em> to the
                            internal state. The outside world interacts only through a controlled public
                            interface — like a car's steering wheel hiding its complex mechanics.
                        </Typography>

                        <ConceptCard title="The Capsule Analogy" color="#a855f7">
                            <CodeBlock>{`//  Without encapsulation:
//    Anyone can set   account.balance = -99999;   ← NO validation!
//    Anyone can read  account.pin;                 ← SECURITY HOLE!
//
//  With encapsulation:
//    account.withdraw(500, correctPin);  ← goes through controlled method
//    account.getBalance(correctPin);     ← PIN verified before revealing
//    account.balance = -99999;  ← ❌ COMPILE ERROR: 'balance' is private
//
//  ┌─────────────────────────────────────────────┐
//  │               BankAccount                   │
//  │  ┌─────────────────────────────────────┐    │
//  │  │  PRIVATE (hidden implementation)    │    │
//  │  │   double balance                    │    │
//  │  │   int    pin                        │    │
//  │  │   void   log(string msg)            │    │
//  │  └─────────────────────────────────────┘    │
//  │  PUBLIC interface (what users see)          │
//  │   deposit(amount)   withdraw(amount, pin)   │
//  │   getBalance(pin)   printStatement()        │
//  └─────────────────────────────────────────────┘`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={encapTab} onChange={(_, v) => setEncapTab(v)} tabs={encapTabs.map(t => t.label)} />
                        <CodeBlock>{encapTabs[encapTab].code}</CodeBlock>

                        {/* Benefits grid */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🏆</span>} title="Benefits of Encapsulation" />
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 2 }}>
                            {[
                                { icon: "🛡️", title: "Data Protection",     color: "#f0883e", body: "Private members cannot be corrupted by outside code. Invariants (rules about valid state) are always enforced through setters." },
                                { icon: "🔧", title: "Easy Maintenance",     color: "#58a6ff", body: "Internal implementation can change freely without affecting code that uses the class — only the public interface is a contract." },
                                { icon: "🧪", title: "Testability",          color: "#7ee787", body: "Well-encapsulated classes have clear inputs (public methods) and outputs — easy to unit test in isolation." },
                                { icon: "📖", title: "Readability",          color: "#a855f7", body: "Public interface documents exactly what the class can do. Users don't need to understand internal details to use it correctly." },
                                { icon: "🔒", title: "Security",             color: "#fbbf24", body: "Sensitive data (passwords, PINs, financial data) stays private. Access is controlled and logged through methods." },
                                { icon: "♻️", title: "Reusability",         color: "#e879f9", body: "A well-encapsulated class is self-contained. Drop it into any project — its internals won't surprise or conflict with anything." },
                            ].map((b, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${b.color}33`, borderRadius: "12px", p: "14px 16px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                                        <Typography sx={{ fontSize: "1.1rem" }}>{b.icon}</Typography>
                                        <Typography sx={{ fontWeight: 700, color: b.color, fontSize: "0.83rem" }}>{b.title}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "0.8rem", opacity: 0.7, lineHeight: 1.7 }}>{b.body}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              CHEATSHEET
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            OOP Quick Reference Cheatsheet
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3 }}>
                            {oopChecklist.map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        {block.category}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.82rem", opacity: 0.82, lineHeight: 1.6 }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Four pillars recap */}
                        <Box sx={{ mt: 4 }}>
                            <Typography sx={{ fontWeight: 800, color: "goldenrod", fontSize: "0.88rem", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                The Four Pillars — Side by Side
                            </Typography>
                            <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                                {!isMobile && <TableHeader columns={["Pillar", "Core Idea", "Key Benefit", "C++ Example"]} />}
                                {fourPillars.map((p, i) =>
                                    isMobile ? (
                                        <MobileDataCard key={i}>
                                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                                                <Typography sx={{ fontSize: "1.1rem" }}>{p.icon}</Typography>
                                                <Typography sx={{ fontWeight: 700, color: p.color, fontSize: "0.88rem" }}>{p.pillar}</Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", mb: 0.4 }}>{p.tagline}</Typography>
                                            <Typography sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.75rem" }}>{p.example}</Typography>
                                        </MobileDataCard>
                                    ) : (
                                        <Box key={i} sx={{ display: "grid", gridTemplateColumns: "130px 1fr 1fr 1fr", gap: "12px", px: 2, py: "11px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: "0.83rem", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography>{p.icon}</Typography>
                                                <Typography sx={{ fontWeight: 700, color: p.color }}>{p.pillar}</Typography>
                                            </Box>
                                            <Box sx={{ opacity: 0.75 }}>{p.tagline}</Box>
                                            <Box sx={{ opacity: 0.75 }}>{p.benefit}</Box>
                                            <Box sx={{ fontFamily: "monospace", color: "#a5d6ff", fontSize: "0.77rem" }}>{p.example}</Box>
                                        </Box>
                                    )
                                )}
                            </Box>
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
